import { useMemo, useState } from 'react';
import type React from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { UserFilterBar } from '../../users';
import { useUsers } from '../../users/hooks/useUsers';
import type { UserFilterValue } from '../../users';
import type {
  AdminNotificationUser,
  AudiencePreviewResponse,
  CampaignAudienceType,
} from '../types';

interface AudienceSectionProps {
  audienceType: CampaignAudienceType;
  selectedUsers: AdminNotificationUser[];
  segmentFilter: UserFilterValue;
  preview?: AudiencePreviewResponse;
  previewLoading: boolean;
  emailEnabled: boolean;
  notFoundEmails: string[];
  resolvingEmails: boolean;
  onAudienceTypeChange: (value: CampaignAudienceType) => void;
  onSelectedUsersChange: (value: AdminNotificationUser[]) => void;
  onSegmentFilterChange: (partial: Partial<UserFilterValue>) => void;
  onResolveEmails: (emails: string[]) => void;
}

const parseEmails = (value: string) =>
  value
    .split(/[\s,;]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean)
    .slice(0, 500);

const userLabel = (user: AdminNotificationUser) => user.email || user.userName || user.publicId;

export const AudienceSection: React.FC<AudienceSectionProps> = ({
  audienceType,
  selectedUsers,
  segmentFilter,
  preview,
  previewLoading,
  emailEnabled,
  notFoundEmails,
  resolvingEmails,
  onAudienceTypeChange,
  onSelectedUsersChange,
  onSegmentFilterChange,
  onResolveEmails,
}) => {
  const [userSearch, setUserSearch] = useState('');
  const [emailPaste, setEmailPaste] = useState('');
  const { data: usersData, isFetching } = useUsers({
    search: userSearch.trim() || undefined,
    page: 1,
    limit: 10,
  });

  const options = useMemo<AdminNotificationUser[]>(
    () =>
      (usersData?.users || []).map((user) => ({
        publicId: user.publicId,
        email: user.email,
        userName: user.userName,
      })),
    [usersData?.users]
  );

  const handleResolve = () => {
    const emails = parseEmails(emailPaste);
    if (emails.length > 0) onResolveEmails(emails);
  };

  return (
    <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Audience</h2>
      <RadioGroup
        row
        value={audienceType}
        onChange={(event) => onAudienceTypeChange(event.target.value as CampaignAudienceType)}
      >
        <FormControlLabel value="USERS" control={<Radio />} label="Users" />
        <FormControlLabel value="SEGMENT" control={<Radio />} label="Segment" />
        <FormControlLabel value="ALL" control={<Radio />} label="All" />
      </RadioGroup>

      {audienceType === 'USERS' && (
        <div className="space-y-3">
          <Autocomplete
            multiple
            filterSelectedOptions
            options={options}
            value={selectedUsers}
            loading={isFetching}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(option, value) => option.publicId === value.publicId}
            onInputChange={(_, value) => setUserSearch(value)}
            onChange={(_, value) => onSelectedUsersChange(value.slice(0, 500))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={userLabel(option)}
                  size="small"
                  {...getTagProps({ index })}
                  key={option.publicId}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Search users" placeholder="Email or username" />
            )}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Paste emails"
            value={emailPaste}
            onChange={(event) => setEmailPaste(event.target.value)}
            placeholder="one@example.com, two@example.com"
          />
          <Button variant="outlined" disabled={resolvingEmails} onClick={handleResolve}>
            Resolve emails
          </Button>
          {notFoundEmails.length > 0 && (
            <Alert severity="warning">
              Not found: {notFoundEmails.slice(0, 20).join(', ')}
              {notFoundEmails.length > 20 ? ` and ${notFoundEmails.length - 20} more` : ''}
            </Alert>
          )}
        </div>
      )}

      {audienceType === 'SEGMENT' && (
        <UserFilterBar value={segmentFilter} onChange={onSegmentFilterChange} />
      )}

      {audienceType === 'ALL' && (
        <Alert severity="info">All current users at send time (anonymous excluded).</Alert>
      )}

      <div className="rounded-md bg-slate-100 p-3 text-sm text-gray-700 dark:bg-slate-900 dark:text-slate-200">
        {previewLoading ? (
          'Estimating recipients...'
        ) : (
          <>
            <div className="font-semibold">
              {emailEnabled
                ? `${preview?.inAppRecipients ?? 0} in-app · ${preview?.email?.recipients ?? 0} email`
                : `${preview?.inAppRecipients ?? 0} recipients`}
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">
              Excluded: {preview?.excluded.anonymous ?? 0} anonymous,{' '}
              {preview?.excluded.inactive ?? 0} inactive, {preview?.excluded.notFound ?? 0} not
              found
            </div>
            {emailEnabled && (
              <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                Email excluded: {preview?.email?.excluded.invalidEmail ?? 0} invalid,{' '}
                {preview?.email?.excluded.suppressed ?? 0} suppressed,{' '}
                {preview?.email?.excluded.optedOut ?? 0} opted out
              </div>
            )}
            {(preview?.sample.length ?? 0) > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {preview?.sample.map((user) => (
                  <Chip key={user.publicId} size="small" label={userLabel(user)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
